import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMapPin, FiAward, FiMessageSquare } from "react-icons/fi";
import Avatar from "./Avatar";
import { RatingText } from "./Stars";
import { SERVICE_ICON_MAP } from "../../data/serviceIcons";
import { cardClass, splitList } from "./jobUtils";
import { formatMoney } from "../../api";

// One seller in the "Find Seller" results.
export default function SellerCard({ seller, currency, onMessage, index = 0 }) {
  const services = splitList(seller.services).map((k) => SERVICE_ICON_MAP[k]).filter(Boolean);
  const skills = splitList(seller.skills);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: Math.min(index, 6) * 0.04 }}
      className={`${cardClass} p-5 flex flex-col hover:border-accent-light transition-colors`}
    >
      <div className="flex gap-4">
        <Link to={`/co-work/sellers/${seller.id}`}><Avatar src={seller.avatar} name={seller.name} size={64} /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/co-work/sellers/${seller.id}`} className="font-bold text-lg leading-tight hover:text-accent-light transition-colors block truncate">{seller.name}</Link>
          <p className="text-sm text-gray-300 truncate">{seller.headline || "Seller"}</p>
          <div className="mt-1"><RatingText avg={seller.rating_avg} count={seller.rating_count} size={12} /></div>
        </div>
        {seller.hourly_rate && (
          <div className="text-right shrink-0">
            <p className="text-lg font-bold text-accent-light leading-tight">{currency} {formatMoney(seller.hourly_rate)}</p>
            <p className="text-[11px] text-gray-400">per hour</p>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
        <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 inline-flex items-center gap-1 font-semibold"><FiAward size={11} /> {seller.badge}</span>
        <span>{seller.jobs_done} job{seller.jobs_done === 1 ? "" : "s"} done</span>
        {seller.country && <span className="inline-flex items-center gap-1"><FiMapPin size={11} /> {seller.country}</span>}
      </div>

      {seller.bio && <p className="mt-3 text-sm text-gray-300 line-clamp-2">{seller.bio}</p>}

      {(services.length > 0 || skills.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {services.slice(0, 4).map(({ key, label, Icon }) => (
            <span key={key} className="px-2 py-1 rounded-md bg-accent/10 border border-accent-light/25 text-accent-light text-xs inline-flex items-center gap-1"><Icon size={11} /> {label}</span>
          ))}
          {services.length === 0 && skills.slice(0, 4).map((s) => (
            <span key={s} className="px-2 py-1 rounded-md bg-white/5 border border-navy-border text-gray-300 text-xs">{s}</span>
          ))}
          {services.length > 4 && <span className="px-2 py-1 text-xs text-gray-500">+{services.length - 4} more</span>}
        </div>
      )}

      <div className="mt-auto pt-4 flex gap-2">
        <Link to={`/co-work/sellers/${seller.id}`} className="flex-1 text-center px-4 py-2 rounded-full border border-navy-border text-sm font-semibold hover:border-accent-light">View Profile</Link>
        <button onClick={() => onMessage(seller)} className="flex-1 px-4 py-2 rounded-full bg-brand-gradient text-sm font-semibold inline-flex items-center justify-center gap-1.5">
          <FiMessageSquare size={14} /> Message
        </button>
      </div>
    </motion.div>
  );
}
